import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { ProfileCard } from '@/app/layouts/sidebar/components/ProfileCard';
import { SidebarNav } from '@/app/layouts/sidebar/components/SidebarNav';
import { Button } from '@/shared/components/Button';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { cn } from '@/shared/lib/cn';
import { useIsMobile } from '@/shared/lib/use-media-query';
import { useUiStore } from '@/shared/stores/ui.store';

export const Sidebar = () => {
  const isMobile = useIsMobile();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const setCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const { t } = useTranslation('app');

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile, setCollapsed]);

  const handleNavigate = () => {
    if (isMobile) {
      setCollapsed(true);
    }
  };

  return (
    <>
      {isMobile && !collapsed ? (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-50 animate-backdrop-in bg-ink/30"
          onClick={() => {
            setCollapsed(true);
          }}
        />
      ) : null}
      <aside
        className={cn(
          '@container flex h-dvh shrink-0 flex-col px-3 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] bg-surface',
          collapsed
            ? 'w-16'
            : 'fixed inset-y-0 left-0 z-50 w-full max-w-xs md:static md:z-auto md:w-1/4 md:max-w-md',
        )}
      >
        <div
          className={cn(
            'flex min-h-12 items-center',
            collapsed ? 'justify-center' : 'justify-between gap-2',
          )}
        >
          <div
            className={cn(
              'flex min-w-0 items-center gap-2 overflow-hidden',
              collapsed ? 'hidden' : 'flex',
            )}
          >
            <img
              alt="E-Label Solutions"
              className="h-10 w-auto shrink-0"
              src="/brand/el-logo.png"
            />
            <div className={cn('min-w-0', collapsed ? 'hidden' : 'hidden @min-[264px]:block')}>
              <div className="eyebrow truncate">{t('sidebar.eyebrow')}</div>
              <h2 className="m-0 truncate text-base leading-tight">E-Label Solutions</h2>
            </div>
          </div>
          <Button
            iconOnly
            size="sm"
            variant="ghost"
            className="shrink-0"
            leadingIcon={
              collapsed ? (
                <PanelLeftOpen aria-hidden="true" className="size-4" />
              ) : (
                <PanelLeftClose aria-hidden="true" className="size-4" />
              )
            }
            onClick={() => {
              setCollapsed(!collapsed);
            }}
            aria-label={collapsed ? t('sidebar.expandAria') : t('sidebar.collapseAria')}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          />
        </div>
        <div className="min-h-0 grow overflow-y-auto py-3">
          <SidebarNav collapsed={collapsed} onNavigate={handleNavigate} />
        </div>
        <div className="grid gap-3">
          <LanguageSwitcher collapsed={collapsed} />
          <ProfileCard collapsed={collapsed} />
        </div>
      </aside>
    </>
  );
};
